/**
 * Social service with TanStack Query hooks for likes, comments, and notifications
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import type { Like, Comment, Notification, PaginatedResponse } from '../types';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../lib/api-client';

// ============================================================================
// API Functions
// ============================================================================

/**
 * Like a photo
 */
const likePhoto = async (photoId: string): Promise<void> => {
  await apiClient.post(`/photos/${photoId}/likes`);
};

/**
 * Unlike a photo
 */
const unlikePhoto = async (photoId: string, userId: string): Promise<void> => {
  await apiClient.delete(`/photos/${photoId}/likes/${userId}`);
};

/**
 * Fetch likes for a photo
 */
const fetchPhotoLikes = async (photoId: string): Promise<Like[]> => {
  const { data } = await apiClient.get<{ items: Like[] }>(`/photos/${photoId}/likes`);
  return data.items || [];
};

/**
 * Fetch comments for a photo with pagination
 */
const fetchPhotoComments = async (
  photoId: string,
  cursor?: string
): Promise<PaginatedResponse<Comment>> => {
  const params: Record<string, string> = {};
  if (cursor) {
    params.cursor = cursor;
  }

  const { data } = await apiClient.get<PaginatedResponse<Comment>>(`/photos/${photoId}/comments`, {
    params,
  });

  return {
    items: data.items || [],
    nextCursor: data.nextCursor,
    hasMore: data.hasMore || false,
  };
};

/**
 * Create a comment on a photo
 */
const createComment = async (photoId: string, text: string): Promise<Comment> => {
  const { data } = await apiClient.post<Comment>(`/photos/${photoId}/comments`, { text });
  return data;
};

/**
 * Update a comment
 */
const updateComment = async (commentId: string, text: string): Promise<Comment> => {
  const { data } = await apiClient.put<Comment>(`/comments/${commentId}`, { text });
  return data;
};

/**
 * Delete a comment
 */
const deleteComment = async (commentId: string): Promise<void> => {
  await apiClient.delete(`/comments/${commentId}`);
};

/**
 * Fetch notifications for current user
 */
const fetchNotifications = async (): Promise<Notification[]> => {
  const { data } = await apiClient.get<{ items: Notification[] }>('/notifications');
  return data.items || [];
};

/**
 * Mark notification as read
 */
const markNotificationRead = async (notificationId: string): Promise<void> => {
  await apiClient.put(`/notifications/${notificationId}/read`);
};

/**
 * Mark all notifications as read
 */
const markAllNotificationsRead = async (): Promise<void> => {
  await apiClient.put('/notifications/mark-all-read');
};

// ============================================================================
// TanStack Query Hooks
// ============================================================================

/**
 * Hook to like a photo with optimistic updates
 */
export const useLikePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (photoId: string) => likePhoto(photoId),
    onMutate: async (photoId) => {
      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: ['photos'] });

      // Save previous state for rollback
      const previousSinglePhoto = queryClient.getQueryData(['photos', photoId]);
      const previousFeed = queryClient.getQueryData(['photos', 'feed']);

      // Optimistically update single photo
      queryClient.setQueryData(['photos', photoId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          isLikedByCurrentUser: true,
          likeCount: old.likeCount + 1,
        };
      });

      // Optimistically update feed
      queryClient.setQueryData(['photos', 'feed'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: page.items.map((photo: any) =>
              photo.photoId === photoId
                ? { ...photo, isLikedByCurrentUser: true, likeCount: photo.likeCount + 1 }
                : photo
            ),
          })),
        };
      });

      return { previousSinglePhoto, previousFeed };
    },
    onError: (_err, photoId, context) => {
      // Revert on error
      if (context?.previousSinglePhoto) {
        queryClient.setQueryData(['photos', photoId], context.previousSinglePhoto);
      }
      if (context?.previousFeed) {
        queryClient.setQueryData(['photos', 'feed'], context.previousFeed);
      }
    },
    onSuccess: (_data, photoId) => {
      // Only invalidate likes list, not the photos themselves
      // since we're using optimistic updates and the mock API doesn't persist changes
      queryClient.invalidateQueries({ queryKey: ['likes', photoId] });
    },
  });
};

/**
 * Hook to unlike a photo with optimistic updates
 */
export const useUnlikePhoto = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (photoId: string) => unlikePhoto(photoId, user?.userId || ''),
    onMutate: async (photoId) => {
      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: ['photos'] });

      // Save previous state for rollback
      const previousSinglePhoto = queryClient.getQueryData(['photos', photoId]);
      const previousFeed = queryClient.getQueryData(['photos', 'feed']);

      // Optimistically update single photo
      queryClient.setQueryData(['photos', photoId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          isLikedByCurrentUser: false,
          likeCount: Math.max(0, old.likeCount - 1),
        };
      });

      // Optimistically update feed
      queryClient.setQueryData(['photos', 'feed'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: page.items.map((photo: any) =>
              photo.photoId === photoId
                ? {
                    ...photo,
                    isLikedByCurrentUser: false,
                    likeCount: Math.max(0, photo.likeCount - 1),
                  }
                : photo
            ),
          })),
        };
      });

      return { previousSinglePhoto, previousFeed };
    },
    onError: (_err, photoId, context) => {
      // Revert on error
      if (context?.previousSinglePhoto) {
        queryClient.setQueryData(['photos', photoId], context.previousSinglePhoto);
      }
      if (context?.previousFeed) {
        queryClient.setQueryData(['photos', 'feed'], context.previousFeed);
      }
    },
    onSuccess: (_data, photoId) => {
      // Only invalidate likes list, not the photos themselves
      // since we're using optimistic updates and the mock API doesn't persist changes
      queryClient.invalidateQueries({ queryKey: ['likes', photoId] });
    },
  });
};

/**
 * Hook to fetch likes for a photo
 */
export const usePhotoLikes = (photoId: string) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['likes', photoId],
    queryFn: () => fetchPhotoLikes(photoId),
    enabled: !!photoId && isAuthenticated,
  });
};

/**
 * Hook to fetch comments for a photo with infinite scroll
 */
export const usePhotoComments = (photoId: string) => {
  const { isAuthenticated } = useAuth();

  return useInfiniteQuery({
    queryKey: ['comments', photoId],
    queryFn: ({ pageParam }) => fetchPhotoComments(photoId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!photoId && isAuthenticated,
  });
};

/**
 * Hook to create a comment
 */
export const useCreateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ photoId, text }: { photoId: string; text: string }) =>
      createComment(photoId, text),
    onSuccess: (newComment) => {
      // Optimistically add comment to cache
      queryClient.setQueryData(['comments', newComment.photoId], (old: any) => {
        if (!old) {
          // If no existing data, create initial structure
          return {
            pages: [
              {
                items: [newComment],
                nextCursor: undefined,
                hasMore: false,
              },
            ],
            pageParams: [undefined],
          };
        }
        return {
          ...old,
          pages: old.pages.map((page: any, index: number) =>
            index === old.pages.length - 1 ? { ...page, items: [...page.items, newComment] } : page
          ),
        };
      });

      // Update photo comment count
      queryClient.setQueryData(['photos', newComment.photoId], (old: any) => {
        if (!old) return old;
        return { ...old, commentCount: old.commentCount + 1 };
      });

      // Update feed
      queryClient.setQueryData(['photos', 'feed'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: page.items.map((photo: any) =>
              photo.photoId === newComment.photoId
                ? { ...photo, commentCount: photo.commentCount + 1 }
                : photo
            ),
          })),
        };
      });

      // Invalidate to refetch in background (don't block UI update)
      // Use refetchType: 'none' to prevent immediate refetch that would override our optimistic update
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['comments', newComment.photoId],
          refetchType: 'none',
        });
      }, 100);
    },
  });
};

/**
 * Hook to update a comment
 */
export const useUpdateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, text }: { commentId: string; text: string }) =>
      updateComment(commentId, text),
    onSuccess: (updatedComment) => {
      // Update comment in cache
      queryClient.setQueryData(['comments', updatedComment.photoId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: page.items.map((comment: Comment) =>
              comment.commentId === updatedComment.commentId ? updatedComment : comment
            ),
          })),
        };
      });

      // Invalidate to ensure consistency (don't block UI update)
      // Use refetchType: 'none' to prevent immediate refetch that would override our optimistic update
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['comments', updatedComment.photoId],
          refetchType: 'none',
        });
      }, 100);
    },
  });
};

/**
 * Hook to delete a comment
 */
export const useDeleteComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId }: { commentId: string; photoId: string }) => deleteComment(commentId),
    onSuccess: (_data, variables) => {
      // Remove comment from cache
      queryClient.setQueryData(['comments', variables.photoId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: page.items.filter(
              (comment: Comment) => comment.commentId !== variables.commentId
            ),
          })),
        };
      });

      // Update photo comment count
      queryClient.setQueryData(['photos', variables.photoId], (old: any) => {
        if (!old) return old;
        return { ...old, commentCount: Math.max(0, old.commentCount - 1) };
      });

      // Update feed
      queryClient.setQueryData(['photos', 'feed'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: page.items.map((photo: any) =>
              photo.photoId === variables.photoId
                ? { ...photo, commentCount: Math.max(0, photo.commentCount - 1) }
                : photo
            ),
          })),
        };
      });

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['comments', variables.photoId] });
    },
  });
};

/**
 * Hook to fetch notifications
 */
export const useNotifications = () => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => fetchNotifications(),
    // Poll every 30 seconds for real-time updates
    refetchInterval: 30000,
    enabled: isAuthenticated,
  });
};

/**
 * Hook to mark notification as read
 */
export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => markNotificationRead(notificationId),
    onMutate: async (notificationId) => {
      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: ['notifications'] });

      // Save previous state
      const previousNotifications = queryClient.getQueryData(['notifications']);

      // Optimistically update
      queryClient.setQueryData(['notifications'], (old: Notification[] | undefined) => {
        if (!old) return old;
        return old.map((notification) =>
          notification.notificationId === notificationId
            ? { ...notification, isRead: true }
            : notification
        );
      });

      return { previousNotifications };
    },
    onError: (_err, _variables, context) => {
      // Revert on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(['notifications'], context.previousNotifications);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

/**
 * Hook to mark all notifications as read
 */
export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onMutate: async () => {
      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: ['notifications'] });

      // Save previous state
      const previousNotifications = queryClient.getQueryData(['notifications']);

      // Optimistically update all to read
      queryClient.setQueryData(['notifications'], (old: Notification[] | undefined) => {
        if (!old) return old;
        return old.map((notification) => ({ ...notification, isRead: true }));
      });

      return { previousNotifications };
    },
    onError: (_err, _variables, context) => {
      // Revert on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(['notifications'], context.previousNotifications);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};
